/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

// URL Query Parameter Keys
export const URL_PARAMS = {
  PAGE: "page",
  PAGE_SIZE: "pageSize",
  STATUS: "status",
  KEYWORD: "keyword",
} as const

// Pagination Constants
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: ["10", "20", "50"] as string[],
  DEFAULT_LIMIT_VARIANT_ITEMS: 100,
  ASSIGNED_PRODUCTS_PAGE_SIZE: 20,
}

// Search Constants
export const SEARCH = {
  MIN_KEYWORD_LENGTH: 2, // Minimum characters to trigger search (0 or 2+)
} as const

// Filter Values
export const FILTER_VALUES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const

// Products list copy not covered by Seller Centre / Phrase content spec (Products)
export const PRODUCTS_LIST_STRINGS = {
  // Search
  searchPlaceholder: "Search by item name",

  // Table Columns
  stock: "Stock",

  noProductFound: "No products found",
  noItemsFound: "No items found",

  // Stock Status
  total: "Total",
  available: "Available",
  soldOut: "Sold out",

  filterRevisionRequired: "Revision Required",

  // Product table
  pricePlaceholder: "-",
  loadPaginationFailed:
    "Could not load this page of products. Please try again or go to page 1.",
  updateStatusFailed: "Failed to update status. Please try again.",
  expand: "Expand",
  collapse: "Collapse",
  toggleStatusLabel: "Toggle status for {name}",
} as const

// Product status values (API contract)
export const PRODUCT_STATUS = {
  ACTIVE: "ACTIVE",
  DEACTIVATED: "DEACTIVATED",
  PENDING: "PENDING",
} as const

// Product detail section anchor IDs
export const PRODUCT_SECTION_IDS = {
  VARIATION_LIST: "variation-list",
} as const

// Products table row type discriminators (discriminated union)
export const ROW_TYPE = {
  PRODUCT: "product",
  ITEM: "item",
  LOADING: "loading",
} as const

// Products table row key prefixes and column keys
export const PRODUCT_TABLE_KEYS = {
  ROW_KEY_PROP: "rowKey",
  ROW_KEY_PREFIX_PRODUCT: "product-",
  ROW_KEY_PREFIX_ITEM: "item-",
  ROW_KEY_PREFIX_LOADING: "loading-",
  COLUMN_PRODUCT_INFO: "productInfo",
  COLUMN_PRICE: "price",
  COLUMN_STOCK: "stock",
  COLUMN_ACTIVE: "active",
  COLUMN_ACTION: "action",
} as const

export const DEFAULT_DEBOUNCE_WAIT_TIME = 500

// Storefront limits
export const STOREFRONT_LIMITS = {
  MAX_COLLECTIONS: 20,
  MAX_PRODUCTS_PER_COLLECTION: 1000,
  PREVIEW_PRODUCT_LIMIT: 10,
  DEFAULT_LIMIT_CATEGORIES_API_CALL: 100,
  /** Default cmplxSortOrder for new collections; higher value = appears first (top) */
  DEFAULT_CMPLX_SORT_ORDER: 99,
} as const

// Untranslated Strings - Video Upload
// TODO: Replace with i18n translations in future
export const VIDEO_UPLOAD_STRINGS = {
  loadingUploading: "Uploading...",
  loadingProcessing: "Processing...",
  loadingPreparing: "Preparing...",
  addVideo: "Add video",
  removeVideo: "Remove video",
  guidelineSize: (maxMb: number) => `Size: Max ${maxMb}MB`,
  guidelineDuration: (minSec: number, maxSec: number) =>
    `Duration: ${minSec}-${maxSec}s`,
  guidelineFormat: (extensions: string) => `Format: ${extensions}`,
} as const

export const VIDEO_UPLOAD_POLL = {
  STATUS_INTERVAL_MS: 10_000,
  MAX_STATUS_ATTEMPTS: 36,
} as const

export const VIDEO_CONSTRAINTS = {
  MAX_SIZE_MB: 30,
  MIN_DURATION_SEC: 10,
  MAX_DURATION_SEC: 60,
  ACCEPTED_TYPES: ["video/mp4", "video/quicktime"],
  ACCEPTED_EXTENSIONS: ".mp4,.mov",
}

export const VIDEO_UPLOAD_ERROR_MESSAGES = {
  invalidMimeType: "Format must be MP4 or MOV.",
  invalidContainer: "Invalid video file format.",
  sizeExceeded: (maxMb: number) => `Size must not exceed ${maxMb}MB.`,
  durationTooShort: (minSec: number) =>
    `Video must be at least ${minSec} seconds.`,
  durationTooLong: (maxSec: number) =>
    `Video must not exceed ${maxSec} seconds.`,
  processingTimeout: "Video processing timed out. Please try again.",
  processingFailed: "Video processing failed. Please try again.",
  statusCheckFailed: "Failed to check video status.",
  uploadFailed: "Video upload failed. Please try again.",
  canvasContextUnavailable: "Canvas 2D context not available.",
  unableToLoadVideoFile: "Unable to load video file.",
} as const

/** Ant Design / Duxton `notification.error` duration unit is seconds. */
export const SELLER_CENTER_ERROR_NOTIFICATION_DURATION_SECONDS = 10

/** Key used to identify the merchant ID for merchantGroup-level users in the multi-location feature. */
export const ECOMMERCE_MAIN_MERCHANT_ID_KEY = "main"

export const STORE_SELECTOR_STRINGS = {
  allStores: "All stores",
} as const
